import type { DeploymentOutcome, DeploymentRelease, DeploymentStage, FailureCause, FailureInterpretation, LiveSiteHealth, ServingRelation } from '../../api';
import { statusFor } from '../../labels';
import { EvidenceActions, ImpactLabel, NextSteps, OperationSummary, SummaryFact, SummaryFacts, type NextStep } from '../operation/OperationSummary';
import { actionLabel } from '../operation/actionLabels';
import { databaseWords, phaseOfStage, phaseWords, trafficWords, type ImpactAssessment } from '../operation/impact';
import { targetPath, type SectionPath } from '../project/operationalHealth';
import { agoShort } from '../status/ago';
import { deploymentConclusion } from './conclusion';
import type { StepPlace } from './stageNumbering';

const causeWord: Record<FailureCause, string> = {
  code: 'The store’s code',
  dependency: 'Dependencies or their credentials',
  database: 'The database',
  server_permission: 'A server permission',
  infrastructure: 'The server or its services',
  integration: 'An external service',
  commerce_ops: 'Commerce Ops itself',
  unknown: 'Not known',
};

/** Whether deploying the same commit again helps: a value, not a sentence. */
const retryWord: Record<string, string> = {
  helps: 'Can succeed',
  after_fix: 'After a fix',
  wont_help: 'Fails the same way',
  unknown: 'Not known',
};

export interface DeploymentSummaryProps {
  outcome: DeploymentOutcome;
  serving: ServingRelation;
  impact: ImpactAssessment;
  stages: DeploymentStage[];
  site?: LiveSiteHealth;
  rehearsal?: boolean;
  /** A failed attempt: the server's reading of it, and where its step sits. */
  interpretation?: FailureInterpretation;
  place: StepPlace | null;
  /** The raw reason the record carries, shown only as evidence when nothing interprets it. */
  failureReason?: string | null;
  /** A running attempt: why it is waiting, when it is. */
  waiting?: 'database_approval' | 'agent_offline' | 'input' | null;
  currentStep?: string;
  /** The release serving the environment now, when the server named it. */
  liveRelease?: Pick<DeploymentRelease, 'release_tag' | 'commit_sha'> | null;
  checks: { total: number; failed: number };
  sectionPath?: SectionPath;
  onViewError?: () => void;
  onFullLogs?: () => void;
  onViewChecks?: () => void;
}

/**
 * DeploymentSummary: what the screen shows about one attempt before any
 * stage or log (levels 1 and 2), as fields. The heading says how it ended
 * and carries the impact as a label; the fields say where it stopped, what
 * production is doing, whether traffic and the database were touched, the
 * cause and the next step. Prose appears only where no field can carry it
 * (the cause's detail, behind View details).
 */
export function DeploymentSummary(props: DeploymentSummaryProps) {
  const { outcome, impact } = props;
  const { title, tone } = deploymentConclusion(outcome, impact, Boolean(props.interpretation));
  if (outcome === 'deploying' || outcome === 'queued') return <RunningSummary {...props} title={title} tone={tone} />;
  if (outcome === 'succeeded' || outcome === 'succeeded_with_failures') return <SucceededSummary {...props} title={title} tone={tone} />;
  return <FailedSummary {...props} title={title} tone={tone} />;
}

type Titled = DeploymentSummaryProps & { title: string; tone: ReturnType<typeof deploymentConclusion>['tone'] };

/** The live site as a value: its state word and, when it has checks, passing/total. */
function SiteValue({ site }: { site?: LiveSiteHealth }) {
  if (!site) return <span className="op-value__meta">Not reported</span>;
  const status = statusFor('siteState', site.state);
  return (
    <span className={`op-value op-value--${status.tone}`}>
      <strong>{status.label}</strong>
      {site.checks > 0 ? (
        <span className="op-value__meta">
          {' · '}
          {site.passing}/{site.checks} checks
        </span>
      ) : null}
      {site.last_checked_at ? <span className="op-value__meta"> · {agoShort(site.last_checked_at)}</span> : null}
      {site.stale ? <span className="op-value__meta"> · late</span> : null}
    </span>
  );
}

function ProductionFact({ site }: { site?: LiveSiteHealth }) {
  return (
    <SummaryFact label="Production" testId="interpretation-site">
      <SiteValue site={site} />
    </SummaryFact>
  );
}

function TrafficAndDatabase({ impact }: { impact: ImpactAssessment }) {
  return (
    <>
      <SummaryFact label="Traffic" testId="fact-traffic">
        {trafficWords[impact.traffic]}
      </SummaryFact>
      <SummaryFact label="Database" testId="fact-database">
        {databaseWords[impact.database]}
      </SummaryFact>
    </>
  );
}

function FailedSummary({ title, tone, impact, interpretation, place, stages, site, failureReason, liveRelease, sectionPath, onViewError, onFullLogs, outcome }: Titled) {
  const failedStage = stages.find((stage) => stage.status === 'failed');
  const failed = interpretation?.failed_step;
  const likely = interpretation ? interpretation.confidence === 'likely' || (interpretation.cause !== 'unknown' && interpretation.confidence !== 'unknown') : false;
  const steps: NextStep[] = (interpretation?.actions ?? []).map((action) => ({
    label: action.label,
    detail: action.detail,
    command: action.command,
    to: action.target && sectionPath ? targetPath(action.target, sectionPath) : undefined,
  }));
  // A cancelled attempt has no cause to find and no fix to suggest.
  const showCause = outcome === 'failed' || Boolean(interpretation);
  const hasWhere = Boolean(failed || failedStage);
  const details = Boolean(interpretation?.cause_detail || likely || (interpretation?.evidence && interpretation.evidence.length > 0) || interpretation?.also_changed || (!interpretation && failureReason));
  return (
    <OperationSummary
      tone={tone}
      title={title}
      badge={<ImpactLabel impact={impact} testId="impact-indicator" />}
      testId="failure-interpretation"
      actions={<EvidenceActions onViewError={hasWhere ? onViewError : undefined} onFullLogs={onFullLogs} errorTestId="show-failed-step" logsTestId="show-error-in-log" />}
    >
      <SummaryFacts>
        {hasWhere ? (
          <SummaryFact label="Stage" testId="failure-where">
            {place && place.stageNumber > 0 ? (
              <>
                <strong>{place.stageTitle}</strong>
                <span className="op-value__meta">
                  {' · '}
                  {place.stageNumber}/{place.stageCount}
                </span>
              </>
            ) : (
              <strong>{failedStage?.title ?? failed?.title}</strong>
            )}
          </SummaryFact>
        ) : null}
        {failed ? (
          <SummaryFact label="Step" testId="failure-step">
            <strong>{failed.title}</strong>
            {place ? (
              <span className="op-value__meta">
                {' · '}
                {place.stepNumber}/{place.stepCount}
              </span>
            ) : null}
          </SummaryFact>
        ) : failedStage?.failed_step ? (
          <SummaryFact label="Step" testId="failure-step">
            <strong>{failedStage.failed_step}</strong>
          </SummaryFact>
        ) : null}
        <ProductionFact site={site} />
        <TrafficAndDatabase impact={impact} />
        {liveRelease ? (
          <SummaryFact label="Live release" testId="interpretation-live">
            <code translate="no">{liveRelease.release_tag}</code>
            <span className="op-value__meta"> · {liveRelease.commit_sha.slice(0, 7)}</span>
          </SummaryFact>
        ) : interpretation?.live_now ? (
          // The server's own statement of what serves, when it named no release.
          <SummaryFact label="Live release" testId="interpretation-live" wide>
            {interpretation.live_now}
          </SummaryFact>
        ) : null}
        {showCause ? (
          <SummaryFact label="Cause" testId="interpretation-cause" wide>
            <span className="op-cause">
              <span className={`confidence confidence--${likely ? 'likely' : 'unknown'}`}>{likely ? 'Likely' : 'Unknown'}</span>
              {interpretation && likely ? <strong>{interpretation.cause_title.replace(/\.$/, '')}</strong> : null}
            </span>
            {details ? (
              <details className="disclosure op-cause__more">
                <summary>{actionLabel.viewDetails}</summary>
                {interpretation?.cause_detail ? <p>{interpretation.cause_detail}</p> : null}
                {likely && interpretation ? <p className="muted">{causeWord[interpretation.cause]}, detected from the output. It is an inference, not a confirmed root cause.</p> : null}
                {interpretation?.also_changed ? <p data-testid="interpretation-also">{interpretation.also_changed}</p> : null}
                {interpretation?.evidence && interpretation.evidence.length > 0 ? (
                  <span className="op-evidence" data-testid="interpretation-evidence">
                    <span className="op-evidence__label">From the output</span>
                    {interpretation.evidence.map((line) => (
                      <code key={line} translate="no">
                        {line}
                      </code>
                    ))}
                  </span>
                ) : null}
                {!interpretation && failureReason ? (
                  <span className="op-evidence" data-testid="failure-reason">
                    <span className="op-evidence__label">Recorded reason</span>
                    <code translate="no">{failureReason}</code>
                  </span>
                ) : null}
              </details>
            ) : null}
          </SummaryFact>
        ) : null}
        {steps.length > 0 ? (
          <SummaryFact label="Next" testId="interpretation-actions" wide>
            <NextSteps steps={steps} />
          </SummaryFact>
        ) : null}
        {showCause && interpretation && retryWord[interpretation.retry] ? (
          <SummaryFact label="Retry" testId="interpretation-retry">
            {retryWord[interpretation.retry]}
          </SummaryFact>
        ) : null}
      </SummaryFacts>
    </OperationSummary>
  );
}

const waitingWords: Record<NonNullable<DeploymentSummaryProps['waiting']>, string> = {
  database_approval: 'Database approval',
  agent_offline: 'Agent offline',
  input: 'Your input',
};

function RunningSummary({ title, tone, impact, stages, site, outcome, waiting, currentStep }: Titled) {
  const done = stages.filter((stage) => stage.status === 'succeeded' || stage.status === 'warning' || stage.status === 'skipped').length;
  const current = stages.find((stage) => stage.status === 'running') ?? null;
  const number = current ? stages.indexOf(current) + 1 : null;
  const queued = outcome === 'queued';
  return (
    <OperationSummary tone={tone} title={title} badge={queued ? null : <ImpactLabel impact={impact} testId="impact-indicator" />} testId="progress-summary" live>
      {stages.length > 0 && !queued ? (
        <ol className="stage-track" aria-label={`${done} of ${stages.length} stages done`}>
          {/* Each segment is its own stage, in its own state: a stage the run
              has not finished is never drawn as done because another is. */}
          {stages.map((stage) => (
            <li key={stage.key} className={`stage-track__segment stage-track__segment--${segmentState(stage.status)}`} title={`${stage.title}: ${stageStatusWords(stage.status)}`} />
          ))}
        </ol>
      ) : null}
      <SummaryFacts>
        {!queued ? (
          <SummaryFact label="Stage" testId="progress-stage">
            {current ? (
              <>
                <strong>{current.title}</strong>
                <span className="op-value__meta">
                  {' · '}
                  {number}/{stages.length}
                </span>
              </>
            ) : (
              <span className="op-value__meta">Preparing</span>
            )}
          </SummaryFact>
        ) : null}
        {current ? (
          <SummaryFact label="Phase" testId="progress-phase">
            {phaseWords[phaseOfStage(current.key)]}
          </SummaryFact>
        ) : null}
        {currentStep ? <SummaryFact label="Step">{currentStep}</SummaryFact> : null}
        {waiting ? <SummaryFact label="Waiting for">{waitingWords[waiting]}</SummaryFact> : null}
        <ProductionFact site={site} />
        {queued ? null : <TrafficAndDatabase impact={impact} />}
      </SummaryFacts>
    </OperationSummary>
  );
}

function SucceededSummary({ title, tone, impact, site, checks, onViewChecks }: Titled) {
  return (
    <OperationSummary tone={tone} title={title} badge={impact.level === 'none_observed' ? null : <ImpactLabel impact={impact} testId="impact-indicator" />} testId="success-summary">
      <SummaryFacts>
        <ProductionFact site={site} />
        <SummaryFact label="Checks" testId="success-checks">
          {checks.total === 0 ? (
            <span className="op-value__meta">None ran</span>
          ) : (
            <>
              <strong>
                {checks.total - checks.failed}/{checks.total} passed
              </strong>
              {onViewChecks ? (
                <>
                  {' · '}
                  <button type="button" className="link-button" onClick={onViewChecks}>
                    {actionLabel.viewDetails}
                  </button>
                </>
              ) : null}
            </>
          )}
        </SummaryFact>
        {impact.database === 'modified' ? (
          <SummaryFact label="Database" testId="fact-database">
            {databaseWords.modified}
          </SummaryFact>
        ) : null}
      </SummaryFacts>
    </OperationSummary>
  );
}

function segmentState(status: DeploymentStage['status']): 'done' | 'current' | 'partial' | 'failed' | 'todo' {
  switch (status) {
    case 'succeeded':
    case 'warning':
    case 'skipped':
      return 'done';
    case 'running':
      return 'current';
    case 'partial':
      return 'partial';
    case 'failed':
      return 'failed';
    default:
      return 'todo';
  }
}

function stageStatusWords(status: DeploymentStage['status']): string {
  switch (segmentState(status)) {
    case 'done':
      return 'done';
    case 'current':
      return 'running now';
    case 'partial':
      return 'started, more to do';
    case 'failed':
      return 'failed';
    default:
      return 'not started';
  }
}
